/**
 * Dilution reporting-row selection — a faithful port of OG
 * `SpreadsheetEntryController.ApplyDilutionIsReport` (BTLIMS :22740).
 *
 * WHAT IT DOES. For a set of SDMS result rows, decide which run of each
 * sample×parameter (or QC×parameter) dilution GROUP is the ONE reportable row
 * (`IsReport = True`); every other row in the group is `False`. OG re-runs this on
 * load and after a result/DF edit, so the reportable row follows the analyst's data
 * (the least-dilute run whose result is still within the upper quant limit).
 *
 * THE RULE (per group):
 *   1. Blanket reset every row to False.
 *   2. Group rows:
 *        - QCType == "Sample": by SampleID + Parameter
 *        - otherwise         : by SysSampleCode + OriginalSysSampleCode + Parameter
 *      (QC dilutions keep their own sys-sample lineage — never pooled with the parent
 *       client sample's dilution rows.)
 *   3. Pick the group's reportable row:
 *        - Internal-Standard group (any row InternalStandard >= 1): lowest positive DF
 *          (UQL bound does not apply — ISTDs measure instrument performance).
 *        - Single-row group: that row (no choice; skips UQL validation).
 *        - Non-Sample group with any UQL missing: lowest DF among rows that DO have a
 *          UQL, else lowest DF across the group.
 *        - Otherwise: among rows with NumericResult, DF, UQL all present and DF > 0,
 *          keep those where (DFAdjusted ? NumericResult/DF : NumericResult) <= UQL,
 *          order by DF ascending, take the first. If NONE qualify, the whole group
 *          stays False (OG "flag for Peer Review").
 *   "Lowest DF" ties, and the no-usable-DF fallback, break by the row's original index.
 *
 * This module is PURE (no sheet / network) so the rule is unit-tested in isolation; the
 * caller reads the live values, runs this, and writes the True/False back to the sheet.
 */

/** One result row the rule reasons over. `numericResult`/`df`/`uql` are the LIVE values
 *  (null when blank / non-numeric). `internalStandard` is OG's int flag (>= 1 = ISTD). */
export interface DilutionRow {
  /** Stable identity of the row (e.g. calcRowKey(sys,param,runNo)); echoed in the result. */
  rowId: string;
  /** Original order — the deterministic tie-breaker OG uses (snapshot index). */
  idx: number;
  qcType: string;
  sampleId: string;
  sys: string;
  origSys: string;
  param: string;
  numericResult: number | null;
  df: number | null;
  uql: number | null;
  internalStandard: number;
}

/** Group key (OG :22998-23004): Sample → S|SampleID|Param, else Q|Sys|OrigSys|Param. */
function groupKey(r: DilutionRow): string {
  const isSample = r.qcType.trim().toLowerCase() === 'sample';
  return isSample
    ? `S|${r.sampleId}|${r.param}`
    : `Q|${r.sys}|${r.origSys}|${r.param}`;
}

/** OG `pickLowestDf` (:23009): the row with the lowest positive DF, else the first by idx. */
function pickLowestDf(rows: readonly DilutionRow[]): DilutionRow | null {
  const withDf = rows.filter(r => r.df !== null && r.df > 0)
                     .sort((a, b) => (a.df as number) - (b.df as number));
  if (withDf.length > 0) return withDf[0]!;
  const byIdx = [...rows].sort((a, b) => a.idx - b.idx);
  return byIdx[0] ?? null;
}

/**
 * Compute the reportable rows. Returns the SET of `rowId`s that are `IsReport = True`
 * (every row NOT in the set is False). Pure — mirrors OG's branch order exactly.
 *
 * @param rows          the group's candidate rows (any order; `idx` provides OG's order).
 * @param isDFAdjusted  the template's ResultModel == DFAdjusted (default false = NonDFAdjusted).
 */
export function computeDilutionIsReport(
  rows: readonly DilutionRow[],
  isDFAdjusted = false,
): Set<string> {
  const reportable = new Set<string>();
  if (!Array.isArray(rows) || rows.length === 0) return reportable;

  // Group (Map preserves first-seen order; the pick within a group is order-independent).
  const groups = new Map<string, DilutionRow[]>();
  for (const r of rows) {
    const k = groupKey(r);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(r);
  }

  for (const grp of groups.values()) {
    const isInternalStandardGroup = grp.some(r => r.internalStandard >= 1);
    const isSingleRowGroup = grp.length === 1;
    const isSampleGroup = grp.every(r => r.qcType.trim().toLowerCase() === 'sample');
    const anyUqlMissingInGroup = grp.some(r => r.uql === null);

    let best: DilutionRow | null;

    if (isInternalStandardGroup) {
      best = pickLowestDf(grp);
    } else if (isSingleRowGroup) {
      best = grp[0]!;
    } else if (!isSampleGroup && anyUqlMissingInGroup) {
      const withUql = grp.filter(r => r.uql !== null);
      best = pickLowestDf(withUql.length > 0 ? withUql : grp);
    } else {
      const valid = grp
        .filter(r => r.numericResult !== null && r.df !== null && r.uql !== null && r.df > 0
          && (isDFAdjusted
                ? (r.numericResult as number) / (r.df as number) <= (r.uql as number)
                : (r.numericResult as number) <= (r.uql as number)))
        .sort((a, b) => (a.df as number) - (b.df as number));
      if (valid.length === 0) continue;   // all stay False (Peer Review flag)
      best = valid[0]!;
    }

    if (best) reportable.add(best.rowId);
  }

  return reportable;
}

/** The rowIds of every MULTI-row group in which no run has a NumericResult yet — the on-load apply leaves
 *  those as merged. A single-row group is never skipped: the rule makes it True whatever its result. */
export function rowsOfGroupsWithoutResults(rows: readonly DilutionRow[]): Set<string> {
  const groups = new Map<string, DilutionRow[]>();
  for (const r of rows) {
    const k = groupKey(r);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(r);
  }
  const out = new Set<string>();
  for (const grp of groups.values()) {
    if (grp.length < 2 || grp.some(r => r.numericResult !== null)) continue;
    for (const r of grp) out.add(r.rowId);
  }
  return out;
}

// ── Sheet integration (impure) ───────────────────────────────────────────────
import type { SdmsUniverHandle } from './SdmsUniverSheet';
import { calcRowKey } from './sdmsEngineCompute';
import { isResultSheet, sheetCellText } from './sdmsEngineOverlay';
import { normalizeCfNumFmts } from './sdmsWorkbookIo';

/** Case-insensitive field read from a merge record. */
function pick(rec: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    for (const rk of Object.keys(rec)) {
      if (rk.toLowerCase() === k.toLowerCase()) {
        const v = rec[rk];
        if (v != null) return String(v).trim();
      }
    }
  }
  return '';
}

/** `pick` for MANY reads of the same records within one call: each record's keys are grouped by lower case once
 *  (a reopen record carries ~440 keys, and `pick` lower-cased every one of them on every read — speed,
 *  2026-09-30). Same answer as `pick`: the keys in order, the record's keys in order, the first non-null value. */
function makePicker(): (rec: Record<string, unknown>, ...keys: string[]) => string {
  const index = new WeakMap<object, Map<string, string[]>>();
  return (rec, ...keys) => {
    let byLower = index.get(rec);
    if (!byLower) {
      byLower = new Map();
      for (const rk of Object.keys(rec)) {
        const l = rk.toLowerCase();
        const same = byLower.get(l);
        if (same) same.push(rk); else byLower.set(l, [rk]);
      }
      index.set(rec, byLower);
    }
    for (const k of keys) {
      for (const rk of byLower.get(k.toLowerCase()) ?? []) {
        const v = rec[rk];
        if (v != null) return String(v).trim();
      }
    }
    return '';
  };
}

const num = (s: string): number | null => {
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** "AH8" → { col, row } (1-based); null on anything unparseable. */
function parseA1(a1?: string): { col: number; row: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
  if (!m) return null;
  let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { col, row: parseInt(m[2]!, 10) };
}

/** Whether the template applies the DF-adjusted UQL rule (ResultModel = DFAdjusted). */
export function isDFAdjustedFromRecords(records: readonly Record<string, unknown>[]): boolean {
  for (const r of records) {
    const m = pick(r, 'ResultModel', 'ResultModelName');
    if (m) return m.toLowerCase().includes('dfadjusted') || m === '1';
  }
  return false;
}

/**
 * Build the dilution rows for the rule from the LIVE sheet + the merge records:
 * NumericResult / DF / UQL are read live from the sheet (they cascade from edits);
 * QCType / SampleID / OriginalSysSampleCode / InternalStandard come from the record
 * (static identity fields), matched by calcRowKey(sys, param, runNo).
 */
export async function assembleDilutionRows(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  records: readonly Record<string, unknown>[],
  /** `buf` already parsed — the caller's one parse shared with the write-back (speed, 2026-09-30). */
  parsed?: import('exceljs').Workbook,
): Promise<DilutionRow[]> {
  const par = parseA1(fieldCells.PARAMETER);
  if (!par) return [];
  // OG returns WITHOUT touching IsReport when the template positions no NumericResult field (:22797 —
  // "SampleID, IsReport and NumericResult are mandatory"). Anions 1014 keeps its result in UserDefined20
  // and has no NumericResult position, so OG leaves the stored IsReport as it is; G6 ran the rule on a
  // null result, no run qualified and every group went False (QB26091809_032, 20 groups). (2026-09-24)
  if (!parseA1(fieldCells.NUMERICRESULT)) return [];
  if (parsed) return assembleDilutionRowsFromWorkbook(parsed, fieldCells, records);
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return assembleDilutionRowsFromWorkbook(wb, fieldCells, records);
}

/** `assembleDilutionRows` on a workbook already in memory (read only). */
export function assembleDilutionRowsFromWorkbook(
  wb: import('exceljs').Workbook,
  fieldCells: Record<string, string>,
  records: readonly Record<string, unknown>[],
): DilutionRow[] {
  const par = parseA1(fieldCells.PARAMETER);
  if (!par) return [];
  if (!parseA1(fieldCells.NUMERICRESULT)) return [];   // OG leaves IsReport alone without a NumericResult field (above)
  const sysP = parseA1(fieldCells.SYSSAMPLECODE);
  const runP = parseA1(fieldCells.RUNNO);
  const nrP  = parseA1(fieldCells.NUMERICRESULT);
  const dfP  = parseA1(fieldCells.DF ?? fieldCells.DILUTION);
  const uqlP = parseA1(fieldCells.UQL);
  const pick = makePicker();   // the records are only read here
  const byKey = new Map<string, Record<string, unknown>>();
  for (const r of records) {
    const key = calcRowKey(pick(r, 'SysSampleCode'), pick(r, 'Parameter'), pick(r, 'RunNo'));
    if (!byKey.has(key)) byKey.set(key, r);
  }
  // Reads that create nothing: the rows are only READ here (the step-by-step chain read them from its own
  // throwaway copy), so a missing cell must not be materialised in the shared workbook.
  const cellText = (ws: import('exceljs').Worksheet, row: number, col?: number): string => {
    if (!col) return '';
    return sheetCellText(ws.findRow(row)?.findCell(col));
  };

  const rows: DilutionRow[] = [];
  let idx = 0;
  for (const ws of wb.worksheets) {
    if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
    for (let row = par.row; row <= ws.rowCount; row++) {
      const param = cellText(ws, row, par.col);
      if (!param) break;   // end of the detail band
      const sys = sysP ? cellText(ws, row, sysP.col) : ws.name;
      const runNo = runP ? cellText(ws, row, runP.col) : '';
      const rec = byKey.get(calcRowKey(sys, param, runNo)) ?? {};
      // Live values off the sheet, falling back to the record whenever the sheet gives no number — OG
      // `readDecimal(sheet) ?? readFromDt(dtsample)` per value (:22926). Falling back only when the
      // column was not positioned left an empty / text sheet cell as null, so no run qualified and the
      // whole group stayed False (Anions 1014 QB26091809_032: 20 groups where OG reports one run).
      const numericResult = (nrP ? num(cellText(ws, row, nrP.col)) : null) ?? num(pick(rec, 'NumericResult'));
      const df  = (dfP  ? num(cellText(ws, row, dfP.col))  : null) ?? num(pick(rec, 'DF', 'Dilution'));
      const uql = (uqlP ? num(cellText(ws, row, uqlP.col)) : null) ?? num(pick(rec, 'UQL'));
      rows.push({
        rowId: calcRowKey(sys, param, runNo),
        idx: idx++,
        qcType: pick(rec, 'QCType', 'QcType', 'RunType'),
        sampleId: pick(rec, 'SampleId', 'SampleID', 'StrSampleID'),
        sys,
        origSys: pick(rec, 'OriginalSysSampleCode', 'OriginalSYSSamplecode'),
        param,
        numericResult,
        df,
        uql,
        internalStandard: num(pick(rec, 'InternalStandard')) ?? -1,
      });
    }
  }
  return rows;
}

/** Write "True"/"False" into the ISREPORT column for every band row, keyed by
 *  calcRowKey — rows in `reportable` get "True", the rest "False". Returns cells written. */
export async function writeDilutionIsReportToSheet(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  reportable: ReadonlySet<string>,
  handle: SdmsUniverHandle,
  /** `buf` already parsed (see assembleDilutionRows) — only read here, the writes go to the live sheet. */
  parsed?: import('exceljs').Workbook,
): Promise<number> {
  const par = parseA1(fieldCells.PARAMETER);
  const rep = parseA1(fieldCells.ISREPORT);
  if (!par || !rep) return 0;
  const sysP = parseA1(fieldCells.SYSSAMPLECODE);
  const runP = parseA1(fieldCells.RUNNO);

  let wb = parsed;
  if (!wb) {
    const ExcelJS = (await import('exceljs')).default;
    wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buf);
  }
  const cellText = (ws: import('exceljs').Worksheet, row: number, col?: number): string => {
    if (!col) return '';
    return sheetCellText(ws.getRow(row).getCell(col));
  };
  let count = 0;
  for (const ws of wb.worksheets) {
    if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
    for (let row = par.row; row <= ws.rowCount; row++) {
      const param = cellText(ws, row, par.col);
      if (!param) break;
      const sys = sysP ? cellText(ws, row, sysP.col) : ws.name;
      const runNo = runP ? cellText(ws, row, runP.col) : '';
      const want = reportable.has(calcRowKey(sys, param, runNo)) ? 'True' : 'False';
      if (cellText(ws, row, rep.col) !== want && handle.setCellValue(ws.name, row - 1, rep.col - 1, want)) count++;
    }
  }
  return count;
}

/** Apply the dilution IsReport rule to a merged WORKBOOK (before it reaches the grid) — the
 *  on-load half of OG's ApplyDilutionIsReport (:22740, the client fires "ApplyDilution" when the
 *  document loads and after a NumericResult/DF edit; the edit half is `runDilutionIsReport` on the
 *  page). Every row in the ISReport column becomes True/False per the rule, so a single-run sample
 *  or an MS/MSD row is reportable from the first load and Save persists it that way — before this
 *  the merged flag was the create-time `QCBatchSequence.IsReport` (False for most rows) and a
 *  Save without any result edit persisted False (parity S5, 2026-09-23). Rewrites cells only
 *  when their text differs; returns the untouched buffer when the template has no ISReport
 *  column, no band, or no records. */
export async function applyDilutionIsReportToWorkbook(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  records: readonly Record<string, unknown>[],
): Promise<{ buf: ArrayBuffer; changed: number }> {
  const par = parseA1(fieldCells.PARAMETER);
  const rep = parseA1(fieldCells.ISREPORT);
  if (!par || !rep || !records.length || !parseA1(fieldCells.NUMERICRESULT)) return { buf, changed: 0 };
  // One parse serves both halves (the rows are read from the same bytes the flags are written to).
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const changed = applyDilutionIsReportInWorkbook(wb, fieldCells, records);
  if (!changed) return { buf, changed: 0 };
  normalizeCfNumFmts(wb);
  return { buf: (await wb.xlsx.writeBuffer()) as ArrayBuffer, changed };
}

/** The same rule on a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns how
 *  many ISReport cells changed (0 = `wb` untouched). */
export function applyDilutionIsReportInWorkbook(
  wb: import('exceljs').Workbook,
  fieldCells: Record<string, string>,
  records: readonly Record<string, unknown>[],
): number {
  const par = parseA1(fieldCells.PARAMETER);
  const rep = parseA1(fieldCells.ISREPORT);
  if (!par || !rep || !records.length) return 0;
  const rows = assembleDilutionRowsFromWorkbook(wb, fieldCells, records);
  if (!rows.length) return 0;
  const reportable = computeDilutionIsReport(rows, isDFAdjustedFromRecords(records));
  // On LOAD a group with no result yet keeps the merged flags (the batch's QCBatchSequence.IsReport): the rule
  // has nothing to choose by and would set the whole group False — a fresh 261022002-010 + -010R1 opened with
  // both False where the batch says 010 True (user report 2026-10-22). OG does not run the rule on load at all
  // (SpreadsheetEntryController :5351); it runs after an import / an edit here, once results exist.
  const untouched = rowsOfGroupsWithoutResults(rows);
  const sysP = parseA1(fieldCells.SYSSAMPLECODE);
  const runP = parseA1(fieldCells.RUNNO);
  // Two passes: count first with reads that create nothing, then — only when something changes — the
  // original pass. ExcelJS's getRow / getCell CREATE the row / cell they read, and the step-by-step chain
  // discarded those when nothing changed (it returned the untouched bytes); on the shared in-memory
  // workbook they would be written out (an empty row under the band, styled blank cells).
  const pass = (write: boolean): number => {
    const cellText = (ws: import('exceljs').Worksheet, row: number, col?: number): string => {
      if (!col) return '';
      return sheetCellText(write ? ws.getRow(row).getCell(col) : ws.findRow(row)?.findCell(col));
    };
    let changed = 0;
    for (const ws of wb.worksheets) {
      if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
      for (let row = par.row; row <= ws.rowCount; row++) {
        const param = cellText(ws, row, par.col);
        if (!param) break;
        const sys = sysP ? cellText(ws, row, sysP.col) : ws.name;
        const runNo = runP ? cellText(ws, row, runP.col) : '';
        const key = calcRowKey(sys, param, runNo);
        if (untouched.has(key)) continue;
        const want = reportable.has(key) ? 'True' : 'False';
        const cell = write ? ws.getRow(row).getCell(rep.col) : ws.findRow(row)?.findCell(rep.col);
        const value = cell?.value;
        if (value && typeof value === 'object' && 'formula' in (value as object)) continue;   // a formula owns the cell
        if (cellText(ws, row, rep.col) === want) continue;
        if (write && cell) cell.value = want;
        changed++;
      }
    }
    return changed;
  };
  return pass(false) ? pass(true) : 0;
}
